import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CalendarOff, CalendarCheck, ShoppingBag } from "lucide-react";
import { useAdminAuth, AdminLogin, AdminLoading } from "./AdminAuth";
import AdminBar from "./AdminBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full rounded-sm border border-[#102C1F]/25 bg-[#F4F1EB] px-4 py-2.5 text-sm text-[#102C1F] outline-none transition-colors duration-300 placeholder:text-[#102C1F]/40 focus:border-[#102C1F]";

const WEEKDAYS = [
  ["Lun", 1],
  ["Mar", 2],
  ["Mer", 3],
  ["Jeu", 4],
  ["Ven", 5],
  ["Sam", 6],
  ["Dim", 0],
];

const MONTHS = [
  "Janv.", "Févr.", "Mars", "Avr.", "Mai", "Juin",
  "Juil.", "Août", "Sept.", "Oct.", "Nov.", "Déc.",
];

const SCOPES = [
  { key: "reservations", label: "Réservations", icon: CalendarCheck },
  { key: "orders", label: "Commandes", icon: ShoppingBag },
];

const EMPTY = { dates: [], weekdays: [], months: [] };

const pill = (active) =>
  `rounded-full border px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
    active
      ? "border-red-700 bg-red-700 text-white"
      : "border-[#102C1F]/25 text-[#102C1F]/60 hover:border-[#102C1F]"
  }`;

const HALF_HOURS = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, "0");
  return `${h}:${i % 2 === 0 ? "00" : "30"}`;
});

const fmtHM = (t) => t.replace(":", "h");

export default function AdminFermeturesPage() {
  const [user, setUser] = useAdminAuth();
  const [scope, setScope] = useState("reservations");
  const [byScope, setByScope] = useState({
    reservations: EMPTY,
    orders: EMPTY,
  });
  const [blockDate, setBlockDate] = useState("");
  const [tbType, setTbType] = useState("date");
  const [tbDate, setTbDate] = useState("");
  const [tbWeekdays, setTbWeekdays] = useState([]);
  const [tbStart, setTbStart] = useState("");
  const [tbEnd, setTbEnd] = useState("");

  const blocked = byScope[scope];

  const load = useCallback(async () => {
    const [r, o] = await Promise.all([
      axios.get(`${API}/blocked-days/reservations`),
      axios.get(`${API}/blocked-days/orders`),
    ]);
    setByScope({
      reservations: { ...EMPTY, ...r.data },
      orders: { ...EMPTY, ...o.data },
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    load().catch(() => {});
  }, [user, load]);

  const saveBlocked = async (next, msg) => {
    try {
      const { data } = await axios.put(`${API}/blocked-days/${scope}`, next, {
        withCredentials: true,
      });
      setByScope((prev) => ({ ...prev, [scope]: data }));
      toast.success(msg);
    } catch {
      toast.error("Impossible d'enregistrer. Reconnectez-vous.");
    }
  };

  const blockDay = async () => {
    if (!blockDate) return;
    await saveBlocked(
      { ...blocked, dates: [...blocked.dates, blockDate] },
      "Jour bloqué."
    );
    setBlockDate("");
  };

  const unblockDay = (d) =>
    saveBlocked(
      { ...blocked, dates: blocked.dates.filter((x) => x !== d) },
      "Jour débloqué."
    );

  const toggleWeekday = (w) =>
    saveBlocked(
      {
        ...blocked,
        weekdays: blocked.weekdays.includes(w)
          ? blocked.weekdays.filter((x) => x !== w)
          : [...blocked.weekdays, w],
      },
      "Jours de la semaine mis à jour."
    );

  const toggleMonth = (m) =>
    saveBlocked(
      {
        ...blocked,
        months: blocked.months.includes(m)
          ? blocked.months.filter((x) => x !== m)
          : [...blocked.months, m],
      },
      "Mois mis à jour."
    );

  const addTimeBlock = async () => {
    if (
      !tbStart ||
      !tbEnd ||
      (tbType === "date" && !tbDate) ||
      (tbType === "weekday" && tbWeekdays.length === 0)
    ) {
      toast.error("Choisissez la date/le(s) jour(s) et les deux horaires.");
      return;
    }
    const blocks =
      tbType === "date"
        ? [{ date: tbDate, start: tbStart, end: tbEnd }]
        : tbWeekdays.map((w) => ({ weekday: w, start: tbStart, end: tbEnd }));
    await saveBlocked(
      { ...blocked, time_blocks: [...(blocked.time_blocks || []), ...blocks] },
      "Plage horaire bloquée."
    );
    setTbDate("");
    setTbWeekdays([]);
    setTbStart("");
    setTbEnd("");
  };

  const removeTimeBlock = (idx) =>
    saveBlocked(
      {
        ...blocked,
        time_blocks: (blocked.time_blocks || []).filter((_, i) => i !== idx),
      },
      "Plage horaire débloquée."
    );

  if (user === null) return <AdminLoading />;

  if (user === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F4F1EB] px-5">
        <AdminLogin onSuccess={setUser} title="Jours fermés" />
      </div>
    );
  }

  return (
    <div
      data-testid="admin-fermetures-page"
      className="min-h-screen bg-[#F4F1EB] text-[#102C1F]"
    >
      <AdminBar />
      <header className="bg-[#102C1F] text-[#F4F1EB]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-10">
          <span className="flex items-center gap-3 font-display text-xl italic md:text-2xl">
            <CalendarOff size={20} /> Jours fermés
          </span>
          <Link
            data-testid="fermetures-dashboard-link"
            to="/admin"
            className="rounded-full border border-[#F4F1EB]/30 px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-[#F4F1EB] hover:text-[#102C1F]"
          >
            Gestion
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-10 px-5 py-12 md:px-10">
        <div className="flex flex-wrap gap-2" data-testid="fermetures-scope-tabs">
          {SCOPES.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              data-testid={`fermetures-tab-${key}`}
              onClick={() => setScope(key)}
              className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                scope === key
                  ? "border-[#102C1F] bg-[#102C1F] text-[#F4F1EB]"
                  : "border-[#102C1F]/25 text-[#102C1F]/60 hover:border-[#102C1F]"
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
        <p className="-mt-6 text-xs text-[#102C1F]/50" data-testid="fermetures-scope-hint">
          {scope === "reservations"
            ? "Ces blocages s'appliquent uniquement à la page Réserver."
            : "Ces blocages s'appliquent uniquement à la page Commander."}
        </p>

        <section>
          <p className="mb-3 text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
            Jours précis
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <input
              data-testid={`blocked-date-input-${scope}`}
              type="date"
              value={blockDate}
              onChange={(e) => setBlockDate(e.target.value)}
              className={`${field} w-auto`}
            />
            <button
              data-testid={`blocked-add-btn-${scope}`}
              onClick={blockDay}
              className="rounded-full bg-[#102C1F] px-5 py-2.5 text-xs uppercase tracking-[0.25em] text-[#F4F1EB] transition-colors duration-300 hover:bg-[#1B4030]"
            >
              Bloquer
            </button>
            {blocked.dates.map((d, i) => (
              <span
                key={d}
                data-testid={`blocked-day-chip-${scope}-${i}`}
                className="flex items-center gap-2 rounded-full border border-[#102C1F]/25 px-4 py-2 text-xs"
              >
                {d.split("-").reverse().join("/")}
                <button
                  data-testid={`blocked-remove-${scope}-${i}`}
                  onClick={() => unblockDay(d)}
                  aria-label="Débloquer"
                  className="text-red-700 transition-colors duration-300 hover:text-red-900"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </section>

        <section>
          <p className="mb-3 text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
            Jours de la semaine (toutes les semaines)
          </p>
          <div className="flex flex-wrap gap-2">
            {WEEKDAYS.map(([label, w]) => (
              <button
                key={w}
                data-testid={`blocked-weekday-${scope}-${w}`}
                onClick={() => toggleWeekday(w)}
                className={pill(blocked.weekdays.includes(w))}
              >
                {label}
              </button>
            ))}
          </div>
        </section>

        <section>
          <p className="mb-3 text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
            Mois entiers (ex : vacances)
          </p>
          <div className="flex flex-wrap gap-2">
            {MONTHS.map((label, idx) => (
              <button
                key={idx + 1}
                data-testid={`blocked-month-${scope}-${idx + 1}`}
                onClick={() => toggleMonth(idx + 1)}
                className={pill(blocked.months.includes(idx + 1))}
              >
                {label}
              </button>
            ))}
          </div>
        </section>

        <section>
          <p className="mb-3 text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
            Heures bloquées (ex : pas de service le midi le 24/12)
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-2">
              <button
                data-testid={`timeblock-type-date-${scope}`}
                onClick={() => setTbType("date")}
                className={`rounded-full border px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                  tbType === "date"
                    ? "border-[#102C1F] bg-[#102C1F] text-[#F4F1EB]"
                    : "border-[#102C1F]/25 text-[#102C1F]/60 hover:border-[#102C1F]"
                }`}
              >
                Date précise
              </button>
              <button
                data-testid={`timeblock-type-weekday-${scope}`}
                onClick={() => setTbType("weekday")}
                className={`rounded-full border px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                  tbType === "weekday"
                    ? "border-[#102C1F] bg-[#102C1F] text-[#F4F1EB]"
                    : "border-[#102C1F]/25 text-[#102C1F]/60 hover:border-[#102C1F]"
                }`}
              >
                Jour de la semaine
              </button>
            </div>
            {tbType === "date" ? (
              <input
                data-testid={`timeblock-date-input-${scope}`}
                type="date"
                value={tbDate}
                onChange={(e) => setTbDate(e.target.value)}
                className={`${field} w-auto`}
              />
            ) : (
              <div
                className="flex flex-wrap gap-2"
                data-testid={`timeblock-weekday-select-${scope}`}
              >
                {WEEKDAYS.map(([label, w]) => (
                  <button
                    key={w}
                    data-testid={`timeblock-weekday-${scope}-${w}`}
                    onClick={() =>
                      setTbWeekdays((prev) =>
                        prev.includes(w)
                          ? prev.filter((x) => x !== w)
                          : [...prev, w]
                      )
                    }
                    className={`rounded-full border px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                      tbWeekdays.includes(w)
                        ? "border-[#102C1F] bg-[#102C1F] text-[#F4F1EB]"
                        : "border-[#102C1F]/25 text-[#102C1F]/60 hover:border-[#102C1F]"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <select
              data-testid={`timeblock-start-${scope}`}
              value={tbStart}
              onChange={(e) => setTbStart(e.target.value)}
              className={`${field} w-auto ${!tbStart ? "text-[#102C1F]/40" : ""}`}
            >
              <option value="">Début</option>
              {HALF_HOURS.map((t) => (
                <option key={t} value={t}>
                  {fmtHM(t)}
                </option>
              ))}
            </select>
            <select
              data-testid={`timeblock-end-${scope}`}
              value={tbEnd}
              onChange={(e) => setTbEnd(e.target.value)}
              className={`${field} w-auto ${!tbEnd ? "text-[#102C1F]/40" : ""}`}
            >
              <option value="">Fin</option>
              {HALF_HOURS.map((t) => (
                <option key={t} value={t}>
                  {fmtHM(t)}
                </option>
              ))}
            </select>
            <button
              data-testid={`timeblock-add-btn-${scope}`}
              onClick={addTimeBlock}
              className="rounded-full bg-[#102C1F] px-5 py-2.5 text-xs uppercase tracking-[0.25em] text-[#F4F1EB] transition-colors duration-300 hover:bg-[#1B4030]"
            >
              Bloquer
            </button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {(blocked.time_blocks || []).map((tb, i) => (
              <span
                key={i}
                data-testid={`timeblock-chip-${scope}-${i}`}
                className="flex items-center gap-2 rounded-full border border-[#102C1F]/25 px-4 py-2 text-xs"
              >
                {tb.date
                  ? tb.date.split("-").reverse().join("/")
                  : WEEKDAYS.find(([, w]) => w === tb.weekday)?.[0]}{" "}
                · {tb.start.replace(":", "h")}–{tb.end.replace(":", "h")}
                <button
                  data-testid={`timeblock-remove-${scope}-${i}`}
                  onClick={() => removeTimeBlock(i)}
                  aria-label="Débloquer"
                  className="text-red-700 transition-colors duration-300 hover:text-red-900"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </section>

      </main>
    </div>
  );
}
